import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ANYTIME_PREFERENCE_VALUE } from 'constants/advancedSearch';
import strings from 'strings';

import {
  checkIfItemSelected,
  createSelectedItem,
  defaultFetchOptions,
  extractShortLabel,
  findItemById,
  findPriorityById,
  removeItemFromSelection,
  updateItemPreference,
  updateItemPriority
} from './AdvancedSearch.utils';

export const useOptionsManager = ({
  apiClient,
  endpoint,
  inputValue,
  options,
  typeahead,
  typeaheadLimit,
  typeaheadParams,
  fetchOptions,
  onGetField
}) => {
  const [availableOptions, setAvailableOptions] = useState(options || []);
  const [loading, setLoading] = useState(false);

  const cancelTokenRef = useRef(null);

  useEffect(() => {
    return () => {
      cancelTokenRef.current?.cancel(strings.shared.requests.cancelTokenMessage);
    };
  }, []);

  const resolvedFetchOptions = useMemo(() => {
    if (endpoint) {
      return (inputVal, params) => {
        if (cancelTokenRef.current) {
          cancelTokenRef.current.cancel(strings.shared.requests.cancelTokenMessage);
        }

        const handleCancelToken = cancelSource => {
          cancelTokenRef.current = cancelSource;
        };

        return defaultFetchOptions(inputVal, params, handleCancelToken);
      };
    }
    return fetchOptions;
  }, [fetchOptions, endpoint]);

  const fetchOptionsRef = useRef(resolvedFetchOptions);
  fetchOptionsRef.current = resolvedFetchOptions;

  const hasLoadedRef = useRef(false);
  const lastTypeaheadRef = useRef(typeahead);
  const lastEndpointRef = useRef(endpoint);

  if (lastTypeaheadRef.current !== typeahead) {
    hasLoadedRef.current = false;
    lastTypeaheadRef.current = typeahead;
  }

  if (lastEndpointRef.current !== endpoint) {
    hasLoadedRef.current = false;
    lastEndpointRef.current = endpoint;
  }

  // Load all options on mount (for both typeahead and non-typeahead modes)
  useEffect(() => {
    if (
      fetchOptionsRef.current &&
      typeof fetchOptionsRef.current === 'function' &&
      !hasLoadedRef.current &&
      !typeahead
    ) {
      hasLoadedRef.current = true;
      setLoading(true);
      const loadOptions = async () => {
        try {
          const { limitKey, ...cleanTypeaheadParams } = typeaheadParams;
          const limitParamKey = limitKey || 'limit';
          const params = {
            endpoint,
            apiClient,
            ...cleanTypeaheadParams,
            [limitParamKey]: typeaheadLimit
          };
          const response = await fetchOptionsRef.current('', params);
          setAvailableOptions(response.data);
          if (onGetField) onGetField(response.field);
          setLoading(false);
        } catch (error) {
          if (error?.message !== strings.shared.requests.cancelTokenMessage) {
            setAvailableOptions([]);
            setLoading(false);
          }
        }
      };
      loadOptions();
    } else if (!fetchOptionsRef.current && options !== undefined) {
      setAvailableOptions(options);
    }
  }, [
    typeahead,
    options,
    endpoint,
    apiClient,
    resolvedFetchOptions,
    typeaheadParams,
    typeaheadLimit,
    onGetField
  ]);

  // Search/filter options when typeahead is enabled and user types
  useEffect(() => {
    if (!typeahead || !fetchOptionsRef.current || typeof fetchOptionsRef.current !== 'function') {
      return undefined;
    }

    if (inputValue.length === 0) {
      setAvailableOptions([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const searchOptions = async () => {
      try {
        const { limitKey, ...cleanTypeaheadParams } = typeaheadParams;
        const limitKeyParam = limitKey || 'limit';
        const response = await fetchOptionsRef.current(inputValue, {
          ...cleanTypeaheadParams,
          endpoint,
          apiClient,
          [limitKeyParam]: typeaheadLimit
        });
        setAvailableOptions(response.data);
        if (onGetField) onGetField(response.field);
        setLoading(false);
      } catch (error) {
        if (error?.message !== strings.shared.requests.cancelTokenMessage) {
          setAvailableOptions([]);
          setLoading(false);
        }
      }
    };

    const timeoutId = setTimeout(searchOptions, 300);
    return () => {
      clearTimeout(timeoutId);
      if (cancelTokenRef.current && typeof cancelTokenRef.current.cancel === 'function') {
        cancelTokenRef.current.cancel(strings.shared.requests.cancelTokenMessage);
        cancelTokenRef.current = null;
      }
    };
  }, [inputValue, endpoint, apiClient, typeahead, typeaheadParams, typeaheadLimit, onGetField]);

  return {
    availableOptions,
    loading
  };
};

export const useSelectionManager = ({
  getOptionId,
  onSelectionChange,
  selectedItems,
  selectedPreference,
  selectedPriority,
  isSingleSelect = false
}) => {
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    const handleClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        const isClickInsideAutocomplete = event.target.closest('.MuiAutocomplete-popper');
        const isClickInsidePopover = event.target.closest('.MuiPopover-paper');
        if (!isClickInsideAutocomplete && !isClickInsidePopover) {
          setIsInputVisible(false);
        }
      }
    };

    if (isInputVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return undefined;
  }, [isInputVisible]);

  const handleItemAdd = useCallback(
    (event, newItem) => {
      if (newItem && !checkIfItemSelected({ selectedItems, newItem, getOptionId })) {
        const newSelectedItem = createSelectedItem({
          item: newItem,
          priority: selectedPriority,
          preference: selectedPreference
        });
        onSelectionChange(isSingleSelect ? [newSelectedItem] : [...selectedItems, newSelectedItem]);
        if (isSingleSelect) {
          setInputValue('');
        }
      }
    },
    [
      selectedItems,
      onSelectionChange,
      getOptionId,
      selectedPriority,
      selectedPreference,
      isSingleSelect
    ]
  );

  const handleItemRemove = useCallback(
    itemToRemove => {
      const updatedItems = removeItemFromSelection(selectedItems, itemToRemove);
      onSelectionChange(updatedItems);
    },
    [selectedItems, onSelectionChange]
  );

  const handlePriorityChange = useCallback(
    (itemId, newPriority) => {
      const updatedItems = updateItemPriority({ selectedItems, itemId, newPriority });
      onSelectionChange(updatedItems);
    },
    [selectedItems, onSelectionChange]
  );

  const handlePreferenceChange = useCallback(
    (itemId, newPreference) => {
      const updatedItems = updateItemPreference({ selectedItems, itemId, newPreference });
      onSelectionChange(updatedItems);
    },
    [selectedItems, onSelectionChange]
  );

  const handleOnAdd = () => {
    setInputValue('');
    setIsInputVisible(true);
    setTimeout(() => {
      const input = searchInputRef.current?.querySelector('input');
      if (!input) return;

      input.focus();
      // Trigger Autocomplete dropdown by simulating keyDown event
      const keyEvent = new KeyboardEvent('keydown', {
        key: 'ArrowDown',
        code: 'ArrowDown',
        bubbles: true,
        cancelable: true
      });
      input.dispatchEvent(keyEvent);
    }, 100);
  };

  return {
    inputValue,
    containerRef,
    searchInputRef,
    isInputVisible,
    setInputValue,
    handleItemAdd,
    handleItemRemove,
    handlePriorityChange,
    handlePreferenceChange,
    handleOnAdd
  };
};

export const usePriorityPreferenceManager = ({ defaultPreference, defaultPriority }) => {
  const [selectedPriority, setSelectedPriority] = useState(defaultPriority);
  const [selectedPreference, setSelectedPreference] = useState(defaultPreference);

  return {
    selectedPriority,
    setSelectedPriority,
    selectedPreference,
    setSelectedPreference
  };
};

export const useEditPopover = ({
  onPreferenceChange,
  onPriorityChange,
  priorities,
  selectedItems
}) => {
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);

  const handleEditClick = useCallback((event, itemId) => {
    event.stopPropagation();
    setPopoverAnchor(event.currentTarget);
    setEditingItemId(itemId);
  }, []);

  const handlePopoverClose = useCallback(() => {
    setPopoverAnchor(null);
    setEditingItemId(null);
  }, []);

  const handleChange = useCallback(({ itemId, value, callback }) => {
    if (callback) {
      callback(itemId, value);
    }
    setPopoverAnchor(null);
    setEditingItemId(null);
  }, []);

  const handlePriorityChange = useCallback(
    (itemId, newPriority) =>
      handleChange({ itemId, value: newPriority, callback: onPriorityChange }),
    [handleChange, onPriorityChange]
  );

  const handlePreferenceChange = useCallback(
    (itemId, newPreference) =>
      handleChange({ itemId, value: newPreference, callback: onPreferenceChange }),
    [handleChange, onPreferenceChange]
  );

  const getPriorityLabel = useCallback(
    priorityId => {
      const priority = findPriorityById(priorities, priorityId);
      return extractShortLabel(priority);
    },
    [priorities]
  );

  const getPreferenceLabel = useCallback(preference => {
    return preference !== ANYTIME_PREFERENCE_VALUE
      ? strings.formatString(strings.shared.advancedSearch.prefernceLabel, {
          preference
        })
      : '';
  }, []);

  const getEditingItem = useCallback(() => {
    return findItemById(selectedItems, editingItemId);
  }, [selectedItems, editingItemId]);

  return {
    editingItemId,
    getEditingItem,
    getPreferenceLabel,
    getPriorityLabel,
    handleEditClick,
    handlePopoverClose,
    handlePreferenceChange,
    handlePriorityChange,
    popoverAnchor
  };
};
